import React from "react";
import lightLogoSrc from '../../assets/logo-full.png';
import darkLogoSrc from '../../assets/logo-full-invert.png';
import {styled} from "@mui/material/styles";
import {Box, IconButton, Stack, useMediaQuery} from "@mui/material";
import {DesktopMenu} from "./DesktopMenu";
import MobileMenu from "./MobileMenu";
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import {useThemeMode} from "../../styling/ThemeContext";
import {useCachedImage} from "../../styling/useCachedImage";
import FacebookIcon from '@mui/icons-material/Facebook';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';

const Logo = styled('img')({
    width: '100%',
    maxWidth: '220px',
    height: 'auto',
    alignSelf: 'center',
    minWidth: 0,
    flexShrink: 1,
    // Slightly larger than the demo — crisp on both themes
    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.25))',
});

const GlassHeader = styled(Box)(({theme}) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,

    // Glassmorphism core
    backdropFilter: 'blur(14px) saturate(160%)',
    WebkitBackdropFilter: 'blur(14px) saturate(160%)',

    // Semi-transparent tinted background — adapts to light/dark
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(15, 25, 15, 0.55)'
            : 'rgba(245, 242, 235, 0.55)',

    // Subtle top highlight line for depth
    borderTop: `1px solid ${
        theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(255,255,255,0.7)'
    }`,

    boxShadow:
        theme.palette.mode === 'dark'
            ? '0 4px 32px rgba(0,0,0,0.45)'
            : '0 4px 24px rgba(0,0,0,0.12)',

    padding: '10px 24px 12px 24px',
    transition: 'background-color 0.3s ease',
}));

const Header = () => {
    const {isDark, toggleTheme} = useThemeMode();
    const isMobile = useMediaQuery('(max-width:1290px)');
    const isSmallDesktop = useMediaQuery('(max-width:1569px)');

    const lightLogo = useCachedImage("logo-light", lightLogoSrc);
    const darkLogo = useCachedImage("logo-dark", darkLogoSrc);

    const logo = isDark ? darkLogo ?? darkLogoSrc : lightLogo ?? lightLogoSrc;

    return (
        <GlassHeader>
            <Stack direction="column" alignItems={isMobile ? "flex-start" : "flex-end"}>

                {/* Top row: Logo — spacer — social icons + theme toggle */}
                <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
                    <Logo src={logo} alt="Aventură și Călătorii"/>

                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton
                            component="a"
                            href="https://www.facebook.com/AventuraCalatorii"
                            target="_blank"
                            color="inherit"
                            size="small"
                        >
                            <FacebookIcon fontSize="small"/>
                        </IconButton>

                        <IconButton
                            component="a"
                            href="tel:+4074171780"
                            color="inherit"
                            size="small"
                        >
                            <LocalPhoneIcon fontSize="small"/>
                        </IconButton>

                        <IconButton
                            onClick={toggleTheme}
                            color="inherit"
                            size="small"
                            sx={{ml: 0.5}}
                        >
                            {isDark ? <Brightness7Icon fontSize="small"/> : <Brightness4Icon fontSize="small"/>}
                        </IconButton>

                        {isMobile && <MobileMenu/>}
                    </Stack>
                </Stack>

                {/* Bottom row: nav menu only for desktop */}
                {!isMobile &&
                    <Stack
                        direction={isSmallDesktop ? "column" : "row-reverse"}
                        alignItems="flex-end"
                        sx={{width: '100%', paddingTop: '12px'}}
                    >
                        <DesktopMenu/>
                    </Stack>
                }

            </Stack>
        </GlassHeader>
    );
};

export default Header;